import { bomClient } from '../client/bomClient';
import { awaitValidationRun, runValidationOnce } from '../pages/wizard/validation/validationRun';
import type { BomAnalysisRun } from './useBomAnalysisRun';

/**
 * Step 3a/3b writes: context answers, the validation check and the fix
 * handlers. Fixes apply the change and refresh the BOM components only — no
 * hidden validation re-run. Re-validation is an explicit user action ("Ask
 * Robbie to re-check"); an automatic re-run per fix regenerated every
 * finding's wording, which orphaned the step's local review state.
 */
export const useBomFixActions = (run: BomAnalysisRun) => {
  const { activeBomIdRef, setAnalysisData, refreshRfq } = run;
  const withBom = <A extends unknown[], R>(fn: (bomId: string, ...args: A) => Promise<R>) =>
    async (...args: A): Promise<R | undefined> => {
      const bomId = activeBomIdRef.current;
      return bomId ? fn(bomId, ...args) : undefined;
    };

  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- answers are free-form
    submitContext: withBom(async (bomId, answers: Record<string, any>) => {
      await bomClient.saveContextAnswers(bomId, answers);
      setAnalysisData(await bomClient.getAnalysisStatus(bomId));
    }),
    // One check per BOM: a check already running (202, or RUNNING on arrival)
    // is waited on through its marker, never started again.
    runValidation: withBom(async (bomId) => setAnalysisData(await runValidationOnce(bomId))),
    awaitValidation: withBom(async (bomId) => setAnalysisData(await awaitValidationRun(bomId))),
    replaceComponent: withBom(async (bomId, index: number, alternative: { name: string; reason?: string }) => {
      await bomClient.replaceComponent(bomId, index, alternative);
      await refreshRfq();
    }),
    // The suggested value is saved as a context answer; the next re-check uses it.
    applySuggestion: withBom(async (bomId, field: string, value: string) => {
      await bomClient.saveContextAnswers(bomId, { [field]: value });
    }),
    revertComponent: withBom(async (bomId, index: number, targetVersionIndex: number) => {
      await bomClient.revertComponent(bomId, index, targetVersionIndex);
      await refreshRfq();
    }),
    addComponent: withBom(
      async (bomId, component: { name: string; specifications?: string; quantity?: number; manufacturer?: string }) => {
        // Every add on the validation step is a line Robbie's finding suggested.
        await bomClient.addComponent(bomId, { ...component, origin: 'robbie_suggestion' });
        await refreshRfq();
      },
    ),
    // Fire-and-forget: verdicts stay authoritative locally; a failed persist
    // only costs reload-durability, never the interaction.
    persistReviewState: (states: Record<string, 'applied' | 'skipped' | null>) => {
      const bomId = activeBomIdRef.current;
      if (!bomId) return;
      bomClient.saveAnalysisReviewState(bomId, states).catch(() => undefined);
    },
  };
};

export default useBomFixActions;
